import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { 
  ProjectLifecycleManager, 
  ProjectPhase,
  type ProjectLifecycleState 
} from '@/services/projectLifecycleManager';
import { LifecycleValidator } from '@/lib/lifecycleValidator';
import { blink } from '@/lib/blink';
import { toast } from 'sonner';
import {
  ArrowRight,
  CheckCircle,
  Clock,
  AlertCircle,
  XCircle,
  Play,
  Pause,
  RotateCcw,
  FlaskConical,
  Archive,
  GitBranch,
  Calendar,
  TrendingUp,
  Shield,
  Users
} from 'lucide-react';

const PHASE_ICONS = {
  [ProjectPhase.PLANNING]: GitBranch,
  [ProjectPhase.DEVELOPMENT]: Play,
  [ProjectPhase.TESTING]: FlaskConical,
  [ProjectPhase.DEPLOYMENT]: TrendingUp,
  [ProjectPhase.PRODUCTION]: CheckCircle,
  [ProjectPhase.MAINTENANCE]: Shield,
  [ProjectPhase.END]: Pause,
  [ProjectPhase.R_AND_D]: FlaskConical,
  [ProjectPhase.ARCHIVE]: Archive
};

const PHASE_COLORS = {
  [ProjectPhase.PLANNING]: 'text-blue-500',
  [ProjectPhase.DEVELOPMENT]: 'text-green-500',
  [ProjectPhase.TESTING]: 'text-yellow-500',
  [ProjectPhase.DEPLOYMENT]: 'text-orange-500',
  [ProjectPhase.PRODUCTION]: 'text-purple-500',
  [ProjectPhase.MAINTENANCE]: 'text-cyan-500',
  [ProjectPhase.END]: 'text-gray-500',
  [ProjectPhase.R_AND_D]: 'text-pink-500',
  [ProjectPhase.ARCHIVE]: 'text-slate-500'
};

interface Project {
  id: string;
  name: string;
  state?: ProjectLifecycleState;
}

interface PhaseTransition {
  id: string;
  projectId: string;
  userId: string;
  fromPhase: string;
  toPhase: string;
  transitionedAt: string;
  reason: string;
  validatedBy?: string;
  approvedBy?: string;
  createdAt: string;
}

export function ProjectLifecyclePanel() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [user, setUser] = useState<any>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showRndDialog, setShowRndDialog] = useState(false);
  const [transitions, setTransitions] = useState<PhaseTransition[]>([]);
  const [rndFormData, setRndFormData] = useState({
    rndFocus: '',
    researchDurationWeeks: 8,
    lessonsLearned: '',
    projectAnalysis: '',
    rndPlan: ''
  });

  // Load user and projects
  useEffect(() => {
    const init = async () => {
      try {
        const currentUser = await blink.auth.me();
        setUser(currentUser);

        // For demo, create a sample project
        const demoProjectId = `project_${currentUser.id}_demo`;
        let state = await ProjectLifecycleManager.getCurrentState(demoProjectId);

        if (!state) {
          // Initialize demo project
          state = await ProjectLifecycleManager.initializeProject(
            demoProjectId,
            currentUser.id,
            { name: 'Email AI Agent', description: 'Agentic email management system' }
          );
        }

        setProjects([{
          id: demoProjectId,
          name: 'Email AI Agent',
          state
        }]);

        setSelectedProject({
          id: demoProjectId,
          name: 'Email AI Agent',
          state
        });

        // Load transitions history
        const history = await blink.db.projectPhaseTransitions.list({
          where: { projectId: demoProjectId },
          orderBy: { transitionedAt: 'desc' },
          limit: 20
        });

        setTransitions(history || []);
      } catch (error) {
        console.error('Failed to initialize lifecycle panel:', error);
      }
    };

    init();
  }, []);

  const handlePhaseTransition = async (toPhase: ProjectPhase, requiresApproval: boolean = false) => {
    if (!selectedProject || !user) return;

    // Check authorization (simplified - in production, check user roles)
    if (requiresApproval && !user.role?.includes('admin')) {
      toast.error('This transition requires admin approval');
      return;
    }

    setIsTransitioning(true);

    try {
      const reason = prompt(`Reason for transitioning to ${toPhase}:`);
      if (!reason) {
        setIsTransitioning(false);
        return;
      }

      const result = await ProjectLifecycleManager.transitionPhase(
        selectedProject.id,
        user.id,
        toPhase,
        reason
      );

      if (result.success) {
        toast.success(`Successfully transitioned to ${toPhase}`);
        
        // Update state
        setProjects(prev =>
          prev.map(p =>
            p.id === selectedProject.id
              ? { ...p, state: result.state }
              : p
          )
        );

        setSelectedProject(prev => prev ? { ...prev, state: result.state } : null);

        // Log audit trail
        await blink.db.projectPhaseTransitions.create({
          id: `trans_${Date.now()}`,
          projectId: selectedProject.id,
          userId: user.id,
          fromPhase: selectedProject.state?.currentPhase || ProjectPhase.PLANNING,
          toPhase,
          transitionedAt: new Date().toISOString(),
          reason,
          validatedBy: user.id,
          approvedBy: requiresApproval ? user.id : undefined,
          metadata: JSON.stringify({ timestamp: Date.now() }),
          createdAt: new Date().toISOString()
        });

        // Reload transitions
        const history = await blink.db.projectPhaseTransitions.list({
          where: { projectId: selectedProject.id },
          orderBy: { transitionedAt: 'desc' },
          limit: 20
        });
        setTransitions(history || []);
      } else {
        toast.error(result.error || 'Transition failed');
      }
    } catch (error) {
      console.error('Transition failed:', error);
      toast.error('Failed to transition phase');
    } finally {
      setIsTransitioning(false);
    }
  };

  const handleRndTransition = async () => {
    if (!selectedProject || !user) return;

    setIsTransitioning(true);

    try {
      const lessonsArray = rndFormData.lessonsLearned
        .split('\n')
        .filter(l => l.trim());

      const result = await ProjectLifecycleManager.endProjectAndStartRND(
        selectedProject.id,
        user.id,
        {
          rndFocus: rndFormData.rndFocus,
          researchDurationWeeks: rndFormData.researchDurationWeeks,
          lessonsLearned: lessonsArray,
          projectAnalysis: rndFormData.projectAnalysis,
          rndPlan: rndFormData.rndPlan
        }
      );

      if (result.success) {
        toast.success('Successfully transitioned to R&D phase');
        setShowRndDialog(false);
        
        // Update state
        setProjects(prev =>
          prev.map(p =>
            p.id === selectedProject.id
              ? { ...p, state: result.state }
              : p
          )
        );

        setSelectedProject(prev => prev ? { ...prev, state: result.state } : null);
      } else {
        toast.error(result.error || 'R&D transition failed');
      }
    } catch (error) {
      console.error('R&D transition failed:', error);
      toast.error('Failed to transition to R&D');
    } finally {
      setIsTransitioning(false);
    }
  };

  const updateCompletion = async (percentage: number) => {
    if (!selectedProject || !user) return;

    await ProjectLifecycleManager.updateCompletion(
      selectedProject.id,
      percentage,
      user.id
    );

    const updatedState = await ProjectLifecycleManager.getCurrentState(selectedProject.id);
    setSelectedProject(prev => prev ? { ...prev, state: updatedState } : null);
    toast.success(`Completion updated to ${percentage}%`);
  };

  if (!selectedProject || !selectedProject.state) {
    return <div className="p-6">Loading project lifecycle...</div>;
  }

  const { state } = selectedProject;
  const Icon = PHASE_ICONS[state.currentPhase];
  const color = PHASE_COLORS[state.currentPhase];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <GitBranch className="w-6 h-6" />
          Project Lifecycle Management
        </h2>
        <p className="text-muted-foreground mt-1">
          Manage project phases and transitions with approval workflows
        </p>
      </div>

      {/* Current State Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-3 rounded-lg bg-background ${color}`}>
                <Icon className="w-6 h-6" />
              </div>
              <div>
                <CardTitle>{selectedProject.name}</CardTitle>
                <CardDescription>
                  Current Phase: <span className="font-semibold capitalize">{state.currentPhase}</span>
                </CardDescription>
              </div>
            </div>
            <Badge variant="outline" className={color}>
              {state.currentPhase}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Progress */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Completion</Label>
              <span className="text-sm font-medium">{state.completionPercentage}%</span>
            </div>
            <Progress value={state.completionPercentage} />
            <div className="flex gap-2 mt-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => updateCompletion(Math.min(100, state.completionPercentage + 10))}
              >
                +10%
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => updateCompletion(100)}
              >
                Complete (100%)
              </Button>
            </div>
          </div>

          {/* Phase Info */}
          <div className="grid grid-cols-2 gap-4 pt-4 border-t">
            <div>
              <Label className="text-xs text-muted-foreground">Previous Phase</Label>
              <p className="text-sm font-medium capitalize">
                {state.previousPhase || 'None'}
              </p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Transitioned At</Label>
              <p className="text-sm font-medium">
                {new Date(state.transitionedAt).toLocaleString()}
              </p>
            </div>
          </div>

          {state.reason && (
            <div className="pt-4 border-t">
              <Label className="text-xs text-muted-foreground">Transition Reason</Label>
              <p className="text-sm mt-1">{state.reason}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Available Transitions */}
      <Card>
        <CardHeader>
          <CardTitle>Available Transitions</CardTitle>
          <CardDescription>
            Transition this project to the next phase
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Development */}
            {state.currentPhase === ProjectPhase.PLANNING && (
              <Button
                className="h-auto py-4 flex-col items-start"
                onClick={() => handlePhaseTransition(ProjectPhase.DEVELOPMENT)}
                disabled={isTransitioning}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Play className="w-4 h-4" />
                  <span className="font-semibold">Start Development</span>
                </div>
                <span className="text-xs opacity-80">Begin coding and implementation</span>
              </Button>
            )}

            {/* Testing */}
            {state.currentPhase === ProjectPhase.DEVELOPMENT && (
              <Button
                className="h-auto py-4 flex-col items-start"
                onClick={() => handlePhaseTransition(ProjectPhase.TESTING)}
                disabled={isTransitioning || state.completionPercentage < 80}
              >
                <div className="flex items-center gap-2 mb-2">
                  <FlaskConical className="w-4 h-4" />
                  <span className="font-semibold">Move to Testing</span>
                </div>
                <span className="text-xs opacity-80">
                  {state.completionPercentage < 80 
                    ? 'Requires 80% completion' 
                    : 'Begin QA and testing'}
                </span>
              </Button>
            )}

            {/* Deployment */}
            {state.currentPhase === ProjectPhase.TESTING && (
              <Button
                className="h-auto py-4 flex-col items-start"
                onClick={() => handlePhaseTransition(ProjectPhase.DEPLOYMENT, true)}
                disabled={isTransitioning}
              >
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-4 h-4" />
                  <span className="font-semibold">Deploy</span>
                </div>
                <span className="text-xs opacity-80">Deploy to production (requires approval)</span>
              </Button>
            )}

            {/* Production */}
            {state.currentPhase === ProjectPhase.DEPLOYMENT && (
              <Button
                className="h-auto py-4 flex-col items-start"
                onClick={() => handlePhaseTransition(ProjectPhase.PRODUCTION, true)}
                disabled={isTransitioning}
              >
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-4 h-4" />
                  <span className="font-semibold">Go Live</span>
                </div>
                <span className="text-xs opacity-80">Move to production (requires approval)</span>
              </Button>
            )}

            {/* End Project */}
            {(state.currentPhase === ProjectPhase.PRODUCTION || 
              state.currentPhase === ProjectPhase.MAINTENANCE) && (
              <Button
                className="h-auto py-4 flex-col items-start"
                variant="outline"
                onClick={() => handlePhaseTransition(ProjectPhase.END, true)}
                disabled={isTransitioning}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Pause className="w-4 h-4" />
                  <span className="font-semibold">End Project</span>
                </div>
                <span className="text-xs opacity-80">Conclude project (requires approval)</span>
              </Button>
            )}

            {/* R&D Transition */}
            {state.currentPhase === ProjectPhase.END && (
              <Button
                className="h-auto py-4 flex-col items-start bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600"
                onClick={() => setShowRndDialog(true)}
                disabled={isTransitioning}
              >
                <div className="flex items-center gap-2 mb-2">
                  <FlaskConical className="w-4 h-4" />
                  <span className="font-semibold">Start R&D</span>
                </div>
                <span className="text-xs opacity-80">Begin research phase</span>
              </Button>
            )}

            {/* Restart Development from R&D */}
            {state.currentPhase === ProjectPhase.R_AND_D && (
              <Button
                className="h-auto py-4 flex-col items-start"
                onClick={() => handlePhaseTransition(ProjectPhase.DEVELOPMENT)}
                disabled={isTransitioning}
              >
                <div className="flex items-center gap-2 mb-2">
                  <RotateCcw className="w-4 h-4" />
                  <span className="font-semibold">Resume Development</span>
                </div>
                <span className="text-xs opacity-80">Apply R&D findings</span>
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* R&D Transition Dialog */}
      <Dialog open={showRndDialog} onOpenChange={setShowRndDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Transition to R&D Phase</DialogTitle>
            <DialogDescription>
              Provide comprehensive information about the project completion and research plans
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="rndFocus">Research Focus *</Label>
              <Input
                id="rndFocus"
                placeholder="e.g., AI-powered email classification improvements"
                value={rndFormData.rndFocus}
                onChange={(e) => setRndFormData({ ...rndFormData, rndFocus: e.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="duration">Research Duration (weeks) *</Label>
              <Input
                id="duration"
                type="number"
                min="1"
                max="52"
                value={rndFormData.researchDurationWeeks}
                onChange={(e) => setRndFormData({ ...rndFormData, researchDurationWeeks: parseInt(e.target.value) })}
              />
            </div>

            <div>
              <Label htmlFor="lessons">Lessons Learned (one per line) *</Label>
              <Textarea
                id="lessons"
                rows={4}
                placeholder="Performance critical with 1M+ users&#10;ML model accuracy at 94%&#10;User feedback emphasizes speed"
                value={rndFormData.lessonsLearned}
                onChange={(e) => setRndFormData({ ...rndFormData, lessonsLearned: e.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="analysis">Project Analysis *</Label>
              <Textarea
                id="analysis"
                rows={6}
                placeholder="Comprehensive analysis of project outcomes, metrics, and insights..."
                value={rndFormData.projectAnalysis}
                onChange={(e) => setRndFormData({ ...rndFormData, projectAnalysis: e.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="plan">R&D Plan *</Label>
              <Textarea
                id="plan"
                rows={6}
                placeholder="Week 1-2: Literature review&#10;Week 3-4: Prototype development&#10;Week 5-6: Testing and analysis..."
                value={rndFormData.rndPlan}
                onChange={(e) => setRndFormData({ ...rndFormData, rndPlan: e.target.value })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRndDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleRndTransition}
              disabled={
                isTransitioning ||
                !rndFormData.rndFocus ||
                !rndFormData.lessonsLearned ||
                !rndFormData.projectAnalysis ||
                !rndFormData.rndPlan
              }
            >
              {isTransitioning ? 'Transitioning...' : 'Start R&D Phase'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Audit Trail */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Audit Trail
          </CardTitle>
          <CardDescription>
            Complete history of phase transitions and approvals
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {transitions.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Clock className="w-12 h-12 mx-auto mb-3 opacity-20" />
                <p>No transitions yet</p>
              </div>
            ) : (
              transitions.map((transition, index) => (
                <div
                  key={transition.id}
                  className="flex items-start gap-4 p-4 rounded-lg border bg-card"
                >
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <ArrowRight className="w-5 h-5 text-primary" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="outline" className="capitalize">
                        {transition.fromPhase}
                      </Badge>
                      <ArrowRight className="w-4 h-4 text-muted-foreground" />
                      <Badge variant="default" className="capitalize">
                        {transition.toPhase}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">{transition.reason}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(transition.transitionedAt).toLocaleString()}
                      </span>
                      {transition.approvedBy && (
                        <span className="flex items-center gap-1">
                          <Shield className="w-3 h-3 text-green-500" />
                          Approved
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right text-xs text-muted-foreground">
                    #{transitions.length - index}
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
